import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme, alpha } from "@mui/material/styles";
import { keyframes } from "@emotion/react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PhotoCameraOutlinedIcon from "@mui/icons-material/PhotoCameraOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import StraightenOutlinedIcon from "@mui/icons-material/StraightenOutlined";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import NotesOutlinedIcon from "@mui/icons-material/NotesOutlined";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import PhotoLibraryOutlinedIcon from "@mui/icons-material/PhotoLibraryOutlined";
import ExpenseUserWebcamDialog from "../ExpensesUser/ExpenseUserWebcamDialog";
import { getResponsablesTodos } from "../../api/Proyectos/ProyectosTareasApi";
import { getProveedores } from "../../api/ProveedoresyFabricas/proveedoresApi";
import { getFabricas } from "../../api/ProveedoresyFabricas/fabricasApi";
import {
  getAllMateriales,
  getGrupos,
  getSubgrupos,
  uploadMaterialFotos,
  deleteMaterialFotoById,
  deleteMaterialFoto,
  materialFotoPublicUrl,
} from "../../api/Materialoteca/MaterialotecaApi";
import {
  addFeriaMaterial,
  createFeriaProveedorNuevo,
  deleteFeriaMaterial,
  deleteFeriaProveedor,
  extractFeriaFoto,
  getFeriaEdicion,
  linkFeriaPhotosMaterialoteca,
  linkFeriaProveedor,
  updateFeriaAsistentes,
  updateFeriaEdicion,
  updateFeriaMaterial,
  updateFeriaProveedor,
  uploadFeriaProveedorFoto,
} from "../../api/Ferias/FeriasApi";
import FeriaEstadoChip from "./FeriaEstadoChip";
import { MUESTRA_ESTADOS, materialPrimaryFotoArchivo } from "./feriaStatus";
import MaterialotecaPhotoLightbox from "../Materialoteca/MaterialotecaPhotoLightbox";
import {
  clearFeriaInboxPhotoDrag,
  isFeriaPhotoDragEvent,
  readFeriaInboxPhotoDragIds,
} from "./feriaPhotoDrag";
import {
  materialBelongsToSupplier,
  unassignedFeriaMaterials,
} from "./feriaMaterialBelong";
import FeriaMaterialCard from "./FeriaMaterialCard";
import FeriaDeletePhotoDialog from "./FeriaDeletePhotoDialog";

/** En móvil: input capture. En PC: webcam dialog (capture en desktop puede colgarse). */
const shouldUseCameraCapture = () =>
  typeof navigator !== "undefined" &&
  /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent || "",
  );

const SUPPLIER_INITIAL_STOP = new Set([
  "co",
  "ltd",
  "llc",
  "sl",
  "sa",
  "inc",
  "the",
  "de",
  "la",
  "el",
  "y",
  "and",
  "of",
  "corp",
  "gmbh",
]);

/** Iniciales cortas para rail colapsado (p. ej. «Haining Zhanli…» → HZ). */
export function supplierInitials(name = "") {
  const parts = String(name || "")
    .trim()
    .split(/[\s,/.\-_+]+/)
    .map((p) => p.replace(/[^a-zA-Z0-9À-ÿ]/g, ""))
    .filter((p) => p && !SUPPLIER_INITIAL_STOP.has(p.toLowerCase()));
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ""}${parts[1][0] || ""}`.toUpperCase();
}

const emptySupplierForm = () => ({
  CODPROV: "",
  id_Fabrica: "",
  kind: "trader",
  Nombre: "",
  Pais: "",
  Email: "",
  Telefono: "",
  Direccion: "",
  NVIA: "",
  CP: "",
  Ciudad: "",
  Provincia: "",
  Stand: "",
  photoFile: null,
});

const emptyMaterialForm = () => ({
  id_Materialoteca: "",
  NombreMaterial: "",
  ID_GRUPO: "",
  TipoMaterial: "",
  Composicion: "",
  Peso: "",
  AnchoTirada: "",
  MinimumOrderQty: "",
  Precio: "",
  Proveedor: "",
  id_Fabrica: "",
  MillName: "",
  pedir: false,
  Estado: "catalogado",
  extractName: "",
  photoFile: null,
  /** Todas las fotos a subir a Materialoteca (drag multi). */
  photoFiles: [],
  /** Viene de arrastrar fotos de sesión → sin pickers. */
  fromSessionDrop: false,
});

const motionSafe = {
  "@media (prefers-reduced-motion: reduce)": { animation: "none" },
};

const dialogRise = keyframes`
  from { opacity: 0; transform: translateY(14px) scale(0.97); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`;

const dialogShimmer = keyframes`
  0%, 100% { opacity: 0.55; transform: translateX(0); }
  50% { opacity: 1; transform: translateX(3px); }
`;

/** Opciones de trader/mill ya vinculados a la edición. */
const editionSupplierOptions = (proveedores = []) =>
  (proveedores || [])
    .map((row) => {
      const CODPROV = row.CODPROV || row.proveedor?.CODPROV || null;
      const id_Fabrica = row.id_Fabrica || row.fabrica?.id_Fabrica || null;
      const label =
        row.proveedor?.N_PROVEEDOR || row.fabrica?.NombreFabrica || null;
      if (!label || (!CODPROV && !id_Fabrica)) return null;
      return {
        key: CODPROV ? `t-${CODPROV}` : `f-${id_Fabrica}`,
        CODPROV: CODPROV ? Number(CODPROV) : null,
        id_Fabrica: id_Fabrica ? Number(id_Fabrica) : null,
        label,
        kind: CODPROV ? "trader" : "mill",
      };
    })
    .filter(Boolean);

const ACCENT = "#ffac7e";

const fieldSx = (theme) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: 2,
    backgroundColor:
      theme.palette.mode === "dark"
        ? alpha(theme.palette.common.black, 0.22)
        : alpha(theme.palette.background.paper, 0.92),
  },
  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: alpha(ACCENT, 0.7),
  },
  "& .MuiOutlinedInput-root.Mui-focused": {
    boxShadow: `0 0 0 3px ${alpha(ACCENT, 0.18)}`,
  },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: ACCENT,
  },
});

const FormSection = ({ icon: Icon, title, children }) => {
  const theme = useTheme();
  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.25 }}>
        {Icon ? <Icon sx={{ fontSize: 18, color: ACCENT }} /> : null}
        <Typography
          variant="overline"
          sx={{
            fontWeight: 800,
            letterSpacing: 0.6,
            color: theme.palette.text.secondary,
            lineHeight: 1,
          }}
        >
          {title}
        </Typography>
      </Stack>
      <Stack spacing={1.5}>{children}</Stack>
    </Box>
  );
};

/**
 * Edition detail (right pane): Stage people, suppliers, materials + add dialogs.
 * @param {{ edicionId: number|string, onChanged?: () => void, showHeader?: boolean, focusSection?: string, focusItemId?: number|null, onFocusItem?: (focus: string, focusId: number|null) => void, headerHeight?: number, photoMaterialSeed?: { key: number|string, supplierId: number, file: File, photoIds?: number[] }|null, onPhotoMaterialSeedConsumed?: () => void, onSessionMediaChanged?: () => void, onPhotosAssignedToSupplier?: (photoIds: number[], supplierLabel: string) => void|Promise<void>, dragHoverSupplierId?: number|null }} props
 */
const FeriaEdicionPanel = ({
  edicionId,
  onChanged,
  showHeader = false,
  focusSection = "overview",
  focusItemId = null,
  onFocusItem,
  headerHeight = 104,
  photoMaterialSeed = null,
  onPhotoMaterialSeedConsumed,
  onSessionMediaChanged,
  onPhotosAssignedToSupplier,
  dragHoverSupplierId = null,
  /** Solo lista de proveedores (materiales viven en el centro). */
  suppliersOnly = false,
  /** Rail estrecho: solo iconos con iniciales. */
  compactRail = false,
  onToggleCompact = null,
  /** Drop de fotos del dock (arrastre nativo) sobre un proveedor. */
  onDropSessionPhotos = null,
  /** Pedir abrir editor de material desde el centro. */
  requestEditMaterialId = null,
  onRequestEditMaterialConsumed,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const inputSx = fieldSx(theme);
  const [edicion, setEdicion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [traders, setTraders] = useState([]);
  const [mills, setMills] = useState([]);
  const [materiales, setMateriales] = useState([]);
  const [grupos, setGrupos] = useState([]);
  const [subgrupos, setSubgrupos] = useState([]);
  const [supplierOpen, setSupplierOpen] = useState(false);
  const [materialOpen, setMaterialOpen] = useState(false);
  const [editSupplier, setEditSupplier] = useState(null);
  const [editMaterial, setEditMaterial] = useState(null);
  const [editSupplierForm, setEditSupplierForm] = useState({
    Nombre: "",
    Pais: "",
    Email: "",
    Telefono: "",
    Direccion: "",
    NVIA: "",
    CP: "",
    Ciudad: "",
    Provincia: "",
    Stand: "",
    Notas: "",
    isTrader: true,
  });
  const [editMaterialForm, setEditMaterialForm] = useState({
    NombreMaterial: "",
    Composicion: "",
    MillName: "",
    Proveedor: "",
    id_Fabrica: "",
    Peso: "",
    AnchoTirada: "",
    MinimumOrderQty: "",
    Precio: "",
    Estado: "catalogado",
    Notas: "",
    Fotos: [],
  });
  const [editPhotoBusy, setEditPhotoBusy] = useState(false);
  const [photoLightboxSrc, setPhotoLightboxSrc] = useState(null);
  const [supplierDeleteTarget, setSupplierDeleteTarget] = useState(null);
  const [deletingSupplier, setDeletingSupplier] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [webcamOpen, setWebcamOpen] = useState(false);
  const [webcamKind, setWebcamKind] = useState("card");
  const [supplierMode, setSupplierMode] = useState("existing-trader");
  const [supplierForm, setSupplierForm] = useState(emptySupplierForm);
  const [materialForm, setMaterialForm] = useState(emptyMaterialForm);
  /** Proveedor de la edición seleccionado para filtrar / añadir materiales. */
  const [selectedSupplierId, setSelectedSupplierId] = useState(null);
  const [assignMenu, setAssignMenu] = useState({
    anchor: null,
    feriaMaterialId: null,
  });
  const [highlightMaterialId, setHighlightMaterialId] = useState(null);
  /** Fotos de sesión a vincular tras guardar el material (drag → proveedor). */
  const [linkSessionPhotoIds, setLinkSessionPhotoIds] = useState([]);
  const [supplierDropActiveId, setSupplierDropActiveId] = useState(null);
  const [materialSaveError, setMaterialSaveError] = useState("");
  const [materialSaving, setMaterialSaving] = useState(false);
  const useNativeCamera = shouldUseCameraCapture();

  const patchEditSupplier = (patch) =>
    setEditSupplierForm((prev) => ({ ...prev, ...patch }));
  const patchEditMaterial = (patch) =>
    setEditMaterialForm((prev) => ({ ...prev, ...patch }));
  const patchSupplierForm = (patch) =>
    setSupplierForm((prev) => ({ ...prev, ...patch }));
  const patchMaterialForm = (patch) =>
    setMaterialForm((prev) => ({ ...prev, ...patch }));

  const pickFileInput = (kind) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.onchange = (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      if (kind === "material-photo") {
        patchMaterialForm({ photoFile: file });
        return;
      }
      handlePhotoExtract(file, kind);
    };
    input.click();
  };

  const openCamera = (kind) => {
    if (useNativeCamera) {
      const input = document.createElement("input");
      input.type = "file";
      input.accept = "image/*";
      input.setAttribute("capture", "environment");
      input.onchange = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (kind === "material-photo") {
          patchMaterialForm({ photoFile: file });
          return;
        }
        handlePhotoExtract(file, kind);
      };
      input.click();
      return;
    }
    setWebcamKind(kind);
    setWebcamOpen(true);
  };

  const photoButtons = (kind, { takeLabel, galleryLabel } = {}) => (
    <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
      <Button
        startIcon={<PhotoCameraOutlinedIcon />}
        variant="outlined"
        disabled={extracting && kind !== "material-photo"}
        onClick={() => openCamera(kind)}
        sx={{ textTransform: "none", fontWeight: 700 }}
      >
        {extracting && kind !== "material-photo"
          ? t("FeriasPage.readingPhoto")
          : takeLabel || t("FeriasPage.takePhoto")}
      </Button>
      <Button
        startIcon={<ImageOutlinedIcon />}
        variant="outlined"
        disabled={extracting && kind !== "material-photo"}
        onClick={() => pickFileInput(kind)}
        sx={{ textTransform: "none", fontWeight: 700 }}
      >
        {galleryLabel || t("FeriasPage.chooseGallery")}
      </Button>
    </Stack>
  );

  const load = async () => {
    const data = await getFeriaEdicion(edicionId);
    setEdicion(data);
    return data;
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      getFeriaEdicion(edicionId),
      getResponsablesTodos().catch(() => []),
      getProveedores().catch(() => []),
      getFabricas().catch(() => []),
      getAllMateriales().catch(() => []),
      getGrupos().catch(() => []),
    ])
      .then(([data, u, tr, mi, mats, gr]) => {
        if (cancelled) return;
        setEdicion(data);
        setUsers(Array.isArray(u) ? u : []);
        setTraders(Array.isArray(tr) ? tr : []);
        setMills(Array.isArray(mi) ? mi : []);
        setMateriales(Array.isArray(mats) ? mats : []);
        setGrupos(Array.isArray(gr) ? gr : []);
      })
      .catch((err) => {
        console.error("❌ FeriaEdicionPanel:", err);
        if (!cancelled) setEdicion(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [edicionId]);

  useEffect(() => {
    if (!edicion || focusSection === "overview") return;
    const id =
      focusSection === "suppliers"
        ? "feria-section-suppliers"
        : focusSection === "materials"
          ? "feria-section-materials"
          : null;
    if (!id) return;
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [edicion, focusSection]);

  /** Sincroniza clic del árbol → selección en el panel derecho. */
  useEffect(() => {
    if (!edicion) return;

    if (focusSection === "suppliers") {
      if (focusItemId != null) {
        setSelectedSupplierId(Number(focusItemId));
        setHighlightMaterialId(null);
      }
      return;
    }

    if (focusSection === "materials" && focusItemId != null) {
      const mat = (edicion.materiales || []).find(
        (m) => Number(m.id_FeriaMaterial) === Number(focusItemId),
      );
      if (!mat) return;
      const cod = mat.CODPROV || mat.material?.Proveedor;
      const fab = mat.id_Fabrica;
      const owner = (edicion.proveedores || []).find((p) => {
        const pCod = p.CODPROV || p.proveedor?.CODPROV;
        const pFab = p.id_Fabrica || p.fabrica?.id_Fabrica;
        if (cod && pCod && Number(pCod) === Number(cod)) return true;
        if (fab && pFab && Number(pFab) === Number(fab)) return true;
        return false;
      });
      if (owner) setSelectedSupplierId(owner.id_FeriaProveedor);
      setHighlightMaterialId(Number(focusItemId));
      return;
    }

    if (focusSection === "overview") {
      setHighlightMaterialId(null);
    }
  }, [edicion, focusSection, focusItemId]);

  useEffect(() => {
    if (materialForm.ID_GRUPO) {
      getSubgrupos(materialForm.ID_GRUPO).then(setSubgrupos);
    } else {
      setSubgrupos([]);
    }
  }, [materialForm.ID_GRUPO]);

  const selectedUsers = useMemo(() => {
    const ids = new Set((edicion?.asistentes || []).map((a) => a.id_User));
    return users.filter((u) => ids.has(u.id));
  }, [edicion, users]);

  const supplierOptions = useMemo(
    () => editionSupplierOptions(edicion?.proveedores),
    [edicion?.proveedores],
  );

  const selectedMaterialSupplier = useMemo(() => {
    if (materialForm.Proveedor) {
      return (
        supplierOptions.find(
          (o) => Number(o.CODPROV) === Number(materialForm.Proveedor),
        ) || null
      );
    }
    if (materialForm.id_Fabrica) {
      return (
        supplierOptions.find(
          (o) => Number(o.id_Fabrica) === Number(materialForm.id_Fabrica),
        ) || null
      );
    }
    return null;
  }, [supplierOptions, materialForm.Proveedor, materialForm.id_Fabrica]);

  const sessionDropPreviews = useMemo(() => {
    if (!materialForm.fromSessionDrop) return [];
    const files =
      Array.isArray(materialForm.photoFiles) && materialForm.photoFiles.length
        ? materialForm.photoFiles
        : materialForm.photoFile
          ? [materialForm.photoFile]
          : [];
    return files.map((f, idx) => ({
      key: `${f.name}-${f.size}-${idx}`,
      url: URL.createObjectURL(f),
      isVision: idx === 0,
    }));
  }, [
    materialForm.fromSessionDrop,
    materialForm.photoFiles,
    materialForm.photoFile,
  ]);

  useEffect(() => {
    return () => {
      sessionDropPreviews.forEach((p) => URL.revokeObjectURL(p.url));
    };
  }, [sessionDropPreviews]);

  const selectedEditMaterialSupplier = useMemo(() => {
    if (editMaterialForm.Proveedor) {
      return (
        supplierOptions.find(
          (o) => Number(o.CODPROV) === Number(editMaterialForm.Proveedor),
        ) || null
      );
    }
    if (editMaterialForm.id_Fabrica) {
      return (
        supplierOptions.find(
          (o) => Number(o.id_Fabrica) === Number(editMaterialForm.id_Fabrica),
        ) || null
      );
    }
    return null;
  }, [
    supplierOptions,
    editMaterialForm.Proveedor,
    editMaterialForm.id_Fabrica,
  ]);

  const applySupplierToMaterialForm = (opt, patcher) => {
    if (!opt) {
      patcher({ Proveedor: "", id_Fabrica: "", MillName: "" });
      return;
    }
    if (opt.CODPROV) {
      patcher({
        Proveedor: opt.CODPROV,
        id_Fabrica: "",
        MillName: opt.kind === "mill" ? opt.label : "",
      });
      return;
    }
    patcher({
      Proveedor: "",
      id_Fabrica: opt.id_Fabrica || "",
      MillName: opt.label || "",
    });
  };

  const selectedSupplierRow = useMemo(
    () =>
      (edicion?.proveedores || []).find(
        (p) => Number(p.id_FeriaProveedor) === Number(selectedSupplierId),
      ) || null,
    [edicion?.proveedores, selectedSupplierId],
  );

  const filteredMaterials = useMemo(() => {
    const list = edicion?.materiales || [];
    if (!selectedSupplierRow) return list;
    return list.filter((m) =>
      materialBelongsToSupplier(m, selectedSupplierRow),
    );
  }, [edicion?.materiales, selectedSupplierRow]);

  const unassignedMaterials = useMemo(
    () => unassignedFeriaMaterials(edicion?.materiales, edicion?.proveedores),
    [edicion?.materiales, edicion?.proveedores],
  );

  useEffect(() => {
    if (!highlightMaterialId) return;
    const t = setTimeout(() => {
      const el = document.getElementById(
        `feria-material-${highlightMaterialId}`,
      );
      if (el) el.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }, 80);
    return () => clearTimeout(t);
  }, [highlightMaterialId, selectedSupplierId, filteredMaterials.length]);

  const applySupplierToEmptyMaterial = (base, supplierRow) => {
    if (!supplierRow) return base;
    const cod = supplierRow.CODPROV || supplierRow.proveedor?.CODPROV;
    const fab = supplierRow.id_Fabrica || supplierRow.fabrica?.id_Fabrica;
    const label =
      supplierRow.proveedor?.N_PROVEEDOR ||
      supplierRow.fabrica?.NombreFabrica ||
      "";
    if (cod) {
      base.Proveedor = Number(cod);
      base.id_Fabrica = "";
      base.MillName = "";
    } else if (fab) {
      base.Proveedor = "";
      base.id_Fabrica = Number(fab);
      base.MillName = label;
    }
    return base;
  };

  const openAddMaterial = (supplierRow = null) => {
    const base = applySupplierToEmptyMaterial(emptyMaterialForm(), supplierRow);
    if (supplierRow) {
      setSelectedSupplierId(supplierRow.id_FeriaProveedor);
    }
    setLinkSessionPhotoIds([]);
    setMaterialSaveError("");
    setMaterialForm(base);
    setMaterialOpen(true);
  };

  // Centro pide editar material
  useEffect(() => {
    if (!requestEditMaterialId || !edicion?.materiales) return;
    const row = (edicion.materiales || []).find(
      (m) => Number(m.id_FeriaMaterial) === Number(requestEditMaterialId),
    );
    if (row) openEditMaterial(row);
    onRequestEditMaterialConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestEditMaterialId]);

  // Si desaparece el proveedor seleccionado tras un reload, limpia selección
  useEffect(() => {
    if (!selectedSupplierId || selectedSupplierId === "unassigned") return;
    if (!edicion?.proveedores) return;
    const stillThere = (edicion.proveedores || []).some(
      (p) => Number(p.id_FeriaProveedor) === Number(selectedSupplierId),
    );
    if (!stillThere) setSelectedSupplierId(null);
  }, [edicion?.proveedores, selectedSupplierId]);

  const notifyChanged = async () => {
    const data = await load();
    if (onChanged) onChanged();
    return data;
  };

  const syncEditMaterialFromEdicion = (data, feriaMaterialId) => {
    const refreshed = (data?.materiales || []).find(
      (m) => Number(m.id_FeriaMaterial) === Number(feriaMaterialId),
    );
    if (!refreshed) return;
    setEditMaterial(refreshed);
    setEditMaterialForm((prev) => ({
      ...prev,
      Fotos: refreshed.material?.Fotos || [],
    }));
  };

  const handleEditMaterialPhotos = async (files) => {
    const materialId =
      editMaterial?.id_Materialoteca || editMaterial?.material?.Id;
    if (!materialId || !files?.length) return;
    setEditPhotoBusy(true);
    try {
      await uploadMaterialFotos(materialId, files);
      const data = await notifyChanged();
      syncEditMaterialFromEdicion(data, editMaterial.id_FeriaMaterial);
    } catch (err) {
      console.error("❌ upload fotos material feria:", err);
    } finally {
      setEditPhotoBusy(false);
    }
  };

  const handleRemoveEditMaterialPhoto = async (foto) => {
    const materialId =
      editMaterial?.id_Materialoteca || editMaterial?.material?.Id;
    if (!materialId) return;
    setEditPhotoBusy(true);
    try {
      if (Number.isFinite(Number(foto.id_MaterialotecaFoto))) {
        await deleteMaterialFotoById(materialId, foto.id_MaterialotecaFoto);
      } else {
        await deleteMaterialFoto(materialId);
      }
      const data = await notifyChanged();
      syncEditMaterialFromEdicion(data, editMaterial.id_FeriaMaterial);
    } catch (err) {
      console.error("❌ delete foto material feria:", err);
    } finally {
      setEditPhotoBusy(false);
    }
  };

  const pickEditMaterialPhotos = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.multiple = true;
    input.onchange = (e) => {
      const files = Array.from(e.target.files || []);
      if (files.length) handleEditMaterialPhotos(files);
    };
    input.click();
  };

  const handlePhotoExtract = async (file, kind) => {
    if (!file) return;
    setExtracting(true);
    try {
      const { extracted } = await extractFeriaFoto(file, kind);
      if (kind === "card") {
        setSupplierForm((prev) => ({
          ...prev,
          Nombre: extracted.Nombre || prev.Nombre,
          Pais: extracted.Pais || prev.Pais,
          Email: extracted.Email || prev.Email,
          Telefono: extracted.Telefono || prev.Telefono,
          Direccion: extracted.Direccion || prev.Direccion,
          kind: extracted.kindHint === "mill" ? "mill" : prev.kind,
          photoFile: file,
        }));
        if (extracted.kindHint === "mill") setSupplierMode("new");
      } else {
        setMaterialForm((prev) => ({
          ...prev,
          NombreMaterial: extracted.NombreMaterial || prev.NombreMaterial,
          Composicion: extracted.Composicion || prev.Composicion,
          Peso: extracted.Peso ?? prev.Peso,
          AnchoTirada: extracted.AnchoTirada ?? prev.AnchoTirada,
          MinimumOrderQty: extracted.MinimumOrderQty ?? prev.MinimumOrderQty,
          Precio: extracted.Precio ?? prev.Precio,
          MillName: extracted.MillName || prev.MillName,
          extractName: file.name,
          // La foto de ficha/etiqueta no es la foto del tejido
        }));
      }
    } catch (err) {
      console.error("❌ extract feria:", err);
      if (kind === "card")
        setSupplierForm((prev) => ({ ...prev, photoFile: file }));
      else
        setMaterialForm((prev) => ({
          ...prev,
          extractName: file.name,
        }));
    } finally {
      setExtracting(false);
    }
  };

  /**
   * Alta desde drag/drop: Vision con la 1ª; todas se suben a Materialoteca.
   */
  const openAddMaterialFromPhoto = async (
    supplierRow,
    file,
    photoIds = [],
    files = [],
  ) => {
    if (!supplierRow || !file) return;
    const uploadFiles =
      Array.isArray(files) && files.length
        ? files.filter((f) => f instanceof File)
        : [file];
    const base = applySupplierToEmptyMaterial(emptyMaterialForm(), supplierRow);
    base.photoFile = file;
    base.photoFiles = uploadFiles;
    base.extractName = file.name || "";
    base.fromSessionDrop = true;
    // Subgrupo por defecto (Vision no lo deduce); el usuario puede cambiarlo
    base.TipoMaterial = 1;
    setSelectedSupplierId(supplierRow.id_FeriaProveedor);
    setLinkSessionPhotoIds(
      (photoIds || [])
        .map(Number)
        .filter((id) => Number.isFinite(id) && id > 0),
    );
    setMaterialSaveError("");
    setMaterialForm(base);
    setMaterialOpen(true);
    await handlePhotoExtract(file, "material");
    setMaterialForm((prev) => ({
      ...prev,
      photoFile: prev.photoFile || file,
      photoFiles: prev.photoFiles?.length ? prev.photoFiles : uploadFiles,
      fromSessionDrop: true,
    }));
  };

  const handleSaveSupplier = async () => {
    let created;
    if (supplierMode === "existing-trader") {
      created = await linkFeriaProveedor(edicionId, {
        CODPROV: supplierForm.CODPROV,
        Stand: supplierForm.Stand,
      });
    } else if (supplierMode === "existing-mill") {
      created = await linkFeriaProveedor(edicionId, {
        id_Fabrica: supplierForm.id_Fabrica,
        Stand: supplierForm.Stand,
      });
    } else {
      created = await createFeriaProveedorNuevo(edicionId, {
        kind: supplierForm.kind,
        Nombre: supplierForm.Nombre,
        Pais: supplierForm.Pais,
        Email: supplierForm.Email,
        Telefono: supplierForm.Telefono,
        Direccion: supplierForm.Direccion,
        NVIA: supplierForm.NVIA,
        CP: supplierForm.CP,
        Ciudad: supplierForm.Ciudad,
        Provincia: supplierForm.Provincia,
        Stand: supplierForm.Stand,
      });
    }
    if (supplierForm.photoFile && created?.id_FeriaProveedor) {
      await uploadFeriaProveedorFoto(
        created.id_FeriaProveedor,
        supplierForm.photoFile,
      );
    }
    setSupplierOpen(false);
    await notifyChanged();
  };

  const handleSaveMaterial = async () => {
    setMaterialSaveError("");
    const nombre = String(materialForm.NombreMaterial || "").trim();
    // Vision no rellena subgrupo; igual que el link dialog → default 1
    const tipoMaterial =
      Number(materialForm.TipoMaterial) > 0
        ? Number(materialForm.TipoMaterial)
        : 1;

    if (!materialForm.id_Materialoteca && !nombre) {
      setMaterialSaveError("Falta el nombre del material.");
      return;
    }

    const payload = materialForm.id_Materialoteca
      ? {
          id_Materialoteca: materialForm.id_Materialoteca,
          pedir: materialForm.pedir,
          Estado: materialForm.pedir ? "pendiente_llegar" : materialForm.Estado,
          Proveedor: materialForm.Proveedor || null,
          CODPROV: materialForm.Proveedor || null,
          id_Fabrica: materialForm.id_Fabrica || null,
        }
      : {
          NombreMaterial: nombre,
          TipoMaterial: tipoMaterial,
          Composicion: materialForm.Composicion,
          Peso: materialForm.Peso || null,
          AnchoTirada: materialForm.AnchoTirada || null,
          MinimumOrderQty: materialForm.MinimumOrderQty || null,
          Precio: materialForm.Precio || null,
          Proveedor: materialForm.Proveedor || null,
          CODPROV: materialForm.Proveedor || null,
          id_Fabrica: materialForm.id_Fabrica || null,
          MillName: materialForm.MillName || null,
          pedir: materialForm.pedir,
          Estado: materialForm.pedir ? "pendiente_llegar" : "catalogado",
        };

    setMaterialSaving(true);
    try {
      const created = await addFeriaMaterial(edicionId, payload);
      const toUpload =
        Array.isArray(materialForm.photoFiles) && materialForm.photoFiles.length
          ? materialForm.photoFiles
          : materialForm.photoFile
            ? [materialForm.photoFile]
            : [];
      if (toUpload.length && created?.id_Materialoteca) {
        await uploadMaterialFotos(created.id_Materialoteca, toUpload);
      }
      if (linkSessionPhotoIds.length && created?.id_Materialoteca) {
        try {
          await linkFeriaPhotosMaterialoteca(edicionId, {
            photoIds: linkSessionPhotoIds,
            id_Materialoteca: created.id_Materialoteca,
            CODPROV: materialForm.Proveedor || undefined,
            id_Fabrica: materialForm.id_Fabrica || undefined,
          });
          onSessionMediaChanged?.();
        } catch (err) {
          console.error("❌ link sesión → materialoteca:", err);
        }
      }
      setLinkSessionPhotoIds([]);
      setMaterialOpen(false);
      await notifyChanged();
    } catch (err) {
      const data = err?.response?.data;
      const msg =
        (typeof data === "string" && data) ||
        data?.error ||
        data?.message ||
        err?.message ||
        "No se pudo guardar el material";
      setMaterialSaveError(msg);
    } finally {
      setMaterialSaving(false);
    }
  };

  // Seed externo: arrastrar foto de sesión al proveedor
  useEffect(() => {
    if (!photoMaterialSeed?.key || !edicion?.proveedores) return;
    const row = (edicion.proveedores || []).find(
      (p) =>
        Number(p.id_FeriaProveedor) === Number(photoMaterialSeed.supplierId),
    );
    if (!row || !photoMaterialSeed.file) {
      onPhotoMaterialSeedConsumed?.();
      return;
    }
    let cancelled = false;
    (async () => {
      await openAddMaterialFromPhoto(
        row,
        photoMaterialSeed.file,
        photoMaterialSeed.photoIds || [],
        photoMaterialSeed.files || [],
      );
      if (!cancelled) onPhotoMaterialSeedConsumed?.();
    })();
    return () => {
      cancelled = true;
    };
    // Solo al llegar un seed nuevo
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photoMaterialSeed?.key]);

  if (loading) {
    return (
      <Box
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
        }}
      >
        {showHeader ? (
          <Box
            sx={{
              height: { xs: "auto", md: headerHeight },
              minHeight: { xs: 72, md: headerHeight },
              mb: 1.5,
              borderRadius: 3,
              border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1,
              flexShrink: 0,
              boxSizing: "border-box",
              px: 1.5,
              py: { xs: 1.5, md: 1 },
            }}
          >
            <CircularProgress size={18} />
            <Typography variant="body2" color="text.secondary">
              {t("FeriasPage.loadingEdition")}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ py: 2, display: "flex", alignItems: "center", gap: 1 }}>
            <CircularProgress size={18} />
            <Typography variant="body2" color="text.secondary">
              {t("FeriasPage.loadingEdition")}
            </Typography>
          </Box>
        )}
      </Box>
    );
  }

  if (!edicion) {
    return (
      <Typography variant="body2" color="error" sx={{ py: 1, pl: 1 }}>
        {t("FeriasPage.loadEditionError")}
      </Typography>
    );
  }

  const sectionSx = {
    px: 0,
    py: 0,
    borderRadius: 0,
    border: "none",
    bgcolor: "transparent",
    background: "transparent",
    boxShadow: "none",
    flex: 1,
    minWidth: 0,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
  };

  const panelSectionLabelSx = {
    fontWeight: 800,
    letterSpacing: 0.7,
    color: "text.secondary",
    lineHeight: 1,
    display: "block",
    mb: 1.25,
  };

  const assignMaterialToSupplier = async (feriaMaterialId, supplierRow) => {
    setAssignMenu({ anchor: null, feriaMaterialId: null });
    const cod = supplierRow
      ? supplierRow.CODPROV || supplierRow.proveedor?.CODPROV || null
      : null;
    const fab = supplierRow
      ? supplierRow.id_Fabrica || supplierRow.fabrica?.id_Fabrica || null
      : null;
    const label = supplierRow
      ? supplierRow.proveedor?.N_PROVEEDOR ||
        supplierRow.fabrica?.NombreFabrica ||
        ""
      : "";
    await updateFeriaMaterial(feriaMaterialId, {
      CODPROV: cod ? Number(cod) : null,
      Proveedor: cod ? Number(cod) : null,
      id_Fabrica: fab ? Number(fab) : null,
      MillName: fab && !cod ? label : null,
    });
    await notifyChanged();
  };

  const peopleControls = (
    <Box
      sx={{
        width: "100%",
        p: 2,
        borderRadius: 3,
        border: "1px solid",
        borderColor: alpha(theme.palette.text.primary, 0.06),
        bgcolor: "#fff",
        boxShadow: `0 2px 10px ${alpha("#0b1a24", 0.05)}`,
        backgroundImage: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.07)} 0%, #fff 42%)`,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 1.5 }}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box
            sx={{
              width: 34,
              height: 34,
              borderRadius: 1.75,
              display: "grid",
              placeItems: "center",
              bgcolor: alpha(theme.palette.primary.main, 0.12),
              color: theme.palette.primary.main,
            }}
          >
            <GroupsOutlinedIcon sx={{ fontSize: 18 }} />
          </Box>
          <Box>
            <Typography
              variant="overline"
              sx={{ ...panelSectionLabelSx, mb: 0.15, color: "text.primary" }}
            >
              Equipo
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {selectedUsers.length
                ? `${selectedUsers.length} en feria`
                : "Quién asiste"}
            </Typography>
          </Box>
        </Stack>
        <Chip
          size="small"
          label={edicion.Asistimos ? "Asistimos" : "No asistimos"}
          color={edicion.Asistimos ? "primary" : "default"}
          variant={edicion.Asistimos ? "filled" : "outlined"}
          sx={{ fontWeight: 700, height: 26 }}
        />
      </Stack>
      <Stack spacing={1.5}>
        <FormControlLabel
          sx={{
            m: 0,
            px: 1.25,
            py: 0.85,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.text.primary, 0.03),
          }}
          control={
            <Switch
              size="small"
              checked={!!edicion.Asistimos}
              onChange={async (e) => {
                await updateFeriaEdicion(edicionId, {
                  Asistimos: e.target.checked,
                });
                await notifyChanged();
              }}
            />
          }
          label={
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {t("FeriasPage.attending")}
            </Typography>
          }
        />
        {selectedUsers.length ? (
          <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
            {selectedUsers.map((u) => (
              <Chip
                key={u.id}
                size="small"
                onDelete={async () => {
                  const next = selectedUsers.filter((x) => x.id !== u.id);
                  await updateFeriaAsistentes(
                    edicionId,
                    next.map((x) => x.id),
                  );
                  await notifyChanged();
                }}
                avatar={
                  <Avatar
                    sx={{
                      width: 22,
                      height: 22,
                      fontSize: "0.65rem",
                      fontWeight: 800,
                      bgcolor: alpha(theme.palette.primary.main, 0.2),
                      color: theme.palette.primary.main,
                    }}
                  >
                    {supplierInitials(u.username || "?")}
                  </Avatar>
                }
                label={u.username}
                sx={{ fontWeight: 600, bgcolor: alpha("#0b1a24", 0.04) }}
              />
            ))}
          </Stack>
        ) : null}
        <Autocomplete
          multiple
          size="small"
          fullWidth
          options={users}
          value={selectedUsers}
          getOptionLabel={(u) => u.username || ""}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          onChange={async (_, value) => {
            await updateFeriaAsistentes(
              edicionId,
              value.map((u) => u.id),
            );
            await notifyChanged();
          }}
          renderTags={() => null}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t("FeriasPage.stagePeople")}
              size="small"
              placeholder="Añadir personas…"
            />
          )}
        />
      </Stack>
    </Box>
  );

  const openEditSupplier = (row) => {
    const name =
      row.proveedor?.N_PROVEEDOR ||
      row.fabrica?.NombreFabrica ||
      t("FeriasPage.unnamed");
    setEditSupplier(row);
    setEditSupplierForm({
      Nombre: name,
      Pais: row.proveedor?.PAIS || row.fabrica?.Pais || "",
      Email: row.proveedor?.EMAIL || row.fabrica?.Email || "",
      Telefono: row.proveedor?.TELEFONO || row.fabrica?.Telefono || "",
      Direccion: row.proveedor?.DIRECCION || row.fabrica?.Direccion || "",
      NVIA: row.proveedor?.NVIA || "",
      CP: row.proveedor?.CP || "",
      Ciudad: row.proveedor?.POBLACION || "",
      Provincia: row.proveedor?.PROVINCIA || "",
      Stand: row.Stand || "",
      Notas: row.Notas || "",
      isTrader: Boolean(row.proveedor),
    });
  };

  const openEditMaterial = (row) => {
    setEditMaterial(row);
    setEditMaterialForm({
      NombreMaterial: row.material?.NombreMaterial || "",
      Composicion: row.material?.Composicion || "",
      MillName: row.material?.MillName || "",
      Proveedor: row.CODPROV || row.material?.Proveedor || "",
      id_Fabrica: row.id_Fabrica || "",
      Peso: row.material?.Peso ?? "",
      AnchoTirada: row.material?.AnchoTirada ?? "",
      MinimumOrderQty: row.material?.MinimumOrderQty ?? "",
      Precio: row.material?.Precio ?? "",
      Estado: row.Estado || "catalogado",
      Notas: row.Notas || "",
      Fotos: row.material?.Fotos || [],
    });
  };

  const renderMaterialCard = (row, { showAssignChip = true } = {}) => {
    const thumb = materialFotoPublicUrl(
      materialPrimaryFotoArchivo(row.material),
    );
    const highlighted =
      Number(highlightMaterialId) === Number(row.id_FeriaMaterial);
    const specs = [
      row.material?.Composicion,
      row.material?.Peso != null && row.material?.Peso !== ""
        ? `${row.material.Peso} g/m²`
        : null,
      row.material?.AnchoTirada != null && row.material?.AnchoTirada !== ""
        ? `${row.material.AnchoTirada} cm`
        : null,
    ]
      .filter(Boolean)
      .join(" · ");
    return (
      <Box
        key={row.id_FeriaMaterial}
        id={`feria-material-${row.id_FeriaMaterial}`}
        onClick={() => {
          setHighlightMaterialId(row.id_FeriaMaterial);
          if (onFocusItem) onFocusItem("materials", row.id_FeriaMaterial);
        }}
        sx={{
          p: 1.25,
          borderRadius: 2,
          cursor: "pointer",
          border: "none",
          bgcolor: highlighted
            ? alpha(ACCENT, 0.14)
            : alpha(theme.palette.text.primary, 0.035),
        }}
      >
        <Stack direction="row" spacing={1.1} alignItems="flex-start">
          <Box
            onClick={(e) => {
              if (!thumb) return;
              e.stopPropagation();
              setPhotoLightboxSrc(thumb);
            }}
            sx={{
              width: 52,
              height: 52,
              borderRadius: 1.5,
              flexShrink: 0,
              overflow: "hidden",
              bgcolor: alpha(theme.palette.text.primary, 0.06),
              border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: thumb ? "zoom-in" : "default",
            }}
          >
            {thumb ? (
              <Box
                component="img"
                src={thumb}
                alt=""
                sx={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
            ) : (
              <ImageOutlinedIcon
                sx={{ fontSize: 22, color: "text.disabled" }}
              />
            )}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" alignItems="flex-start" spacing={0.25}>
              <Typography
                sx={{
                  fontWeight: 800,
                  fontSize: "0.85rem",
                  flex: 1,
                  minWidth: 0,
                  lineHeight: 1.3,
                  wordBreak: "break-word",
                }}
              >
                {row.material?.NombreMaterial || `#${row.id_Materialoteca}`}
              </Typography>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  openEditMaterial(row);
                }}
              >
                <EditOutlinedIcon sx={{ fontSize: 17 }} />
              </IconButton>
              <IconButton
                size="small"
                color="error"
                onClick={async (e) => {
                  e.stopPropagation();
                  await deleteFeriaMaterial(row.id_FeriaMaterial);
                  await notifyChanged();
                }}
              >
                <DeleteOutlineIcon sx={{ fontSize: 17 }} />
              </IconButton>
            </Stack>
            {specs ? (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  display: "block",
                  mt: 0.2,
                  lineHeight: 1.35,
                  wordBreak: "break-word",
                }}
              >
                {specs}
              </Typography>
            ) : null}
            <Stack
              direction="row"
              spacing={0.75}
              useFlexGap
              flexWrap="wrap"
              sx={{ mt: 0.75 }}
            >
              <FeriaEstadoChip
                estado={row.Estado}
                editable
                onChange={async (Estado) => {
                  await updateFeriaMaterial(row.id_FeriaMaterial, { Estado });
                  await notifyChanged();
                }}
              />
              {showAssignChip ? (
                <Chip
                  size="small"
                  variant="outlined"
                  label={t("FeriasPage.moveToSupplier")}
                  onClick={(e) => {
                    e.stopPropagation();
                    setAssignMenu({
                      anchor: e.currentTarget,
                      feriaMaterialId: row.id_FeriaMaterial,
                    });
                  }}
                  sx={{ fontWeight: 700 }}
                />
              ) : null}
            </Stack>
          </Box>
        </Stack>
      </Box>
    );
  };

  const openAddSupplierDialog = () => {
    setSupplierMode("existing-trader");
    setSupplierForm(emptySupplierForm());
    setSupplierOpen(true);
  };

  const requestDeleteSupplier = (row) => {
    setSupplierDeleteTarget(row);
  };

  const confirmDeleteSupplier = async () => {
    const id = Number(supplierDeleteTarget?.id_FeriaProveedor);
    if (!Number.isFinite(id) || id <= 0) return;
    setDeletingSupplier(true);
    try {
      await deleteFeriaProveedor(id);
      if (Number(selectedSupplierId) === id) {
        setSelectedSupplierId(null);
      }
      setSupplierDeleteTarget(null);
      await notifyChanged();
    } finally {
      setDeletingSupplier(false);
    }
  };

  const focusSupplierInCenter = (id) => {
    setSelectedSupplierId(id);
    if (onFocusItem) onFocusItem("suppliers", id);
    const el = document.getElementById(`feria-center-supplier-${id}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "nearest" });
  };

  const catalogBlock =
    compactRail && suppliersOnly ? (
      <Box
        sx={{
          ...sectionSx,
          flex: 1,
          overflow: "hidden",
          pt: 0.25,
          px: 0.25,
          alignItems: "center",
        }}
        id="feria-section-catalog"
      >
        <Tooltip title="Expandir proveedores" placement="left">
          <IconButton
            size="small"
            onClick={() => onToggleCompact?.()}
            aria-label="Expandir proveedores"
            sx={{
              mb: 0.75,
              bgcolor: "#fff",
              border: "1px solid",
              borderColor: alpha(theme.palette.text.primary, 0.1),
              boxShadow: `0 1px 2px ${alpha("#0b1a24", 0.06)}`,
              "&:hover": { bgcolor: "#fff" },
            }}
          >
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title={t("FeriasPage.addSupplier")} placement="left">
          <IconButton
            size="small"
            color="primary"
            onClick={openAddSupplierDialog}
            aria-label={t("FeriasPage.addSupplier")}
            sx={{
              mb: 1,
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.18) },
            }}
          >
            <AddIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Stack
          spacing={0.85}
          alignItems="center"
          sx={{
            flex: 1,
            overflowY: "auto",
            minHeight: 0,
            pb: 1,
            width: "100%",
          }}
        >
          {(edicion.proveedores || []).map((row) => {
            const id = Number(row.id_FeriaProveedor);
            const expanded = Number(selectedSupplierId) === id;
            const dropActive =
              Number(supplierDropActiveId) === id ||
              Number(dragHoverSupplierId) === id;
            const name =
              row.proveedor?.N_PROVEEDOR ||
              row.fabrica?.NombreFabrica ||
              t("FeriasPage.unnamed");
            const initials = supplierInitials(name);
            const accent = row.proveedor ? theme.palette.primary.main : ACCENT;

            const acceptSupplierDrop = (e) => {
              const types = [...(e.dataTransfer?.types || [])];
              if (!types.includes("Files") && !isFeriaPhotoDragEvent(e)) {
                return false;
              }
              e.preventDefault();
              e.stopPropagation();
              e.dataTransfer.dropEffect = "copy";
              setSupplierDropActiveId(id);
              return true;
            };

            return (
              <Tooltip
                key={id}
                title={dropActive ? "Suelta aquí" : name}
                placement="left"
              >
                <Box
                  data-feria-supplier-drop={id}
                  onClick={() => focusSupplierInCenter(id)}
                  onDragEnter={acceptSupplierDrop}
                  onDragOver={acceptSupplierDrop}
                  onDragLeave={() => {
                    setSupplierDropActiveId((cur) =>
                      Number(cur) === id ? null : cur,
                    );
                  }}
                  onDrop={async (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setSupplierDropActiveId(null);
                    const photoIds = readFeriaInboxPhotoDragIds(e);
                    clearFeriaInboxPhotoDrag();
                    if (photoIds.length && onDropSessionPhotos) {
                      await onDropSessionPhotos(id, photoIds);
                      return;
                    }
                    const file = Array.from(e.dataTransfer?.files || []).find(
                      (f) =>
                        String(f.type || "")
                          .toLowerCase()
                          .startsWith("image/"),
                    );
                    if (file) {
                      await openAddMaterialFromPhoto(row, file, [], [file]);
                    }
                  }}
                  sx={{
                    width: 44,
                    height: 44,
                    borderRadius: 2,
                    flexShrink: 0,
                    display: "grid",
                    placeItems: "center",
                    cursor: "pointer",
                    userSelect: "none",
                    fontWeight: 800,
                    fontSize: "0.78rem",
                    letterSpacing: 0.2,
                    color: dropActive || expanded ? "#fff" : accent,
                    bgcolor: dropActive
                      ? theme.palette.primary.main
                      : expanded
                        ? accent
                        : alpha(accent, 0.14),
                    border: dropActive
                      ? `2px solid ${theme.palette.primary.main}`
                      : `1px solid ${alpha(accent, 0.22)}`,
                    boxShadow: dropActive
                      ? `0 0 0 3px ${alpha(theme.palette.primary.main, 0.28)}`
                      : `0 1px 2px ${alpha("#0b1a24", 0.06)}`,
                    transition: "background 0.12s ease, box-shadow 0.12s ease",
                  }}
                >
                  {initials}
                </Box>
              </Tooltip>
            );
          })}
        </Stack>
      </Box>
    ) : (
      <Box
        sx={{
          ...sectionSx,
          flex: 1,
          overflow: "hidden",
          pt: suppliersOnly ? 0 : 0.5,
          ...(suppliersOnly
            ? {
                p: 2,
                borderRadius: 3,
                border: "1px solid",
                borderColor: alpha(theme.palette.text.primary, 0.06),
                bgcolor: "#fff",
                boxShadow: `0 2px 10px ${alpha("#0b1a24", 0.05)}`,
                backgroundImage: `linear-gradient(160deg, ${alpha(ACCENT, 0.12)} 0%, #fff 38%)`,
              }
            : {}),
        }}
        id="feria-section-catalog"
      >
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
          sx={{ mb: 1.75 }}
        >
          <Stack direction="row" alignItems="center" spacing={1.1}>
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: 1.75,
                display: "grid",
                placeItems: "center",
                bgcolor: alpha(ACCENT, 0.22),
                color: "#c9784a",
              }}
            >
              <StorefrontOutlinedIcon sx={{ fontSize: 18 }} />
            </Box>
            <Box>
              <Typography
                variant="overline"
                sx={{ ...panelSectionLabelSx, mb: 0.15, color: "text.primary" }}
              >
                Proveedores
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Arrastra fotos encima para crear materiales
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <Chip
              size="small"
              label={(edicion.proveedores || []).length}
              sx={{
                fontWeight: 800,
                height: 26,
                minWidth: 28,
                bgcolor: alpha(theme.palette.text.primary, 0.06),
              }}
            />
            {suppliersOnly && onToggleCompact ? (
              <Tooltip title="Minimizar proveedores">
                <IconButton
                  size="small"
                  onClick={() => onToggleCompact()}
                  aria-label="Minimizar proveedores"
                >
                  <ChevronRightIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : null}
          </Stack>
        </Stack>

        <Button
          fullWidth
          variant="contained"
          startIcon={<AddIcon />}
          onClick={openAddSupplierDialog}
          sx={{
            textTransform: "none",
            fontWeight: 800,
            mb: 1.75,
            py: 1.25,
            borderRadius: 2.5,
            boxShadow: `0 6px 16px ${alpha(theme.palette.primary.main, 0.22)}`,
            "&:hover": {
              boxShadow: `0 8px 20px ${alpha(theme.palette.primary.main, 0.28)}`,
            },
          }}
        >
          {t("FeriasPage.addSupplier")}
        </Button>

        <Stack
          spacing={1.25}
          sx={{ flex: 1, overflowY: "auto", minHeight: 0, pr: 0.5, pb: 0.5 }}
        >
          {(edicion.proveedores || []).length === 0 &&
          (suppliersOnly || unassignedMaterials.length === 0) ? (
            <Box
              sx={{
                py: 3,
                px: 2,
                borderRadius: 2.5,
                border: "1.5px dashed",
                borderColor: alpha(theme.palette.text.primary, 0.16),
                bgcolor: alpha(theme.palette.text.primary, 0.02),
                textAlign: "center",
              }}
            >
              <StorefrontOutlinedIcon
                sx={{ fontSize: 28, color: "text.disabled", mb: 0.75 }}
              />
              <Typography
                variant="body2"
                color="text.secondary"
                fontWeight={600}
              >
                {t("FeriasPage.noSuppliers")}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Añade uno y suelta fotos encima
              </Typography>
            </Box>
          ) : null}

          {(edicion.proveedores || []).map((row) => {
            const id = Number(row.id_FeriaProveedor);
            const expanded = Number(selectedSupplierId) === id;
            const dropActive =
              Number(supplierDropActiveId) === id ||
              Number(dragHoverSupplierId) === id;
            const mats = (edicion.materiales || []).filter((m) =>
              materialBelongsToSupplier(m, row),
            );
            const name =
              row.proveedor?.N_PROVEEDOR ||
              row.fabrica?.NombreFabrica ||
              t("FeriasPage.unnamed");

            const acceptSupplierDrop = (e) => {
              const types = [...(e.dataTransfer?.types || [])];
              if (!types.includes("Files") && !isFeriaPhotoDragEvent(e)) {
                return false;
              }
              e.preventDefault();
              e.stopPropagation();
              e.dataTransfer.dropEffect = "copy";
              setSupplierDropActiveId(id);
              return true;
            };

            const handleSupplierFileDrop = async (e) => {
              e.preventDefault();
              e.stopPropagation();
              setSupplierDropActiveId(null);
              const photoIds = readFeriaInboxPhotoDragIds(e);
              clearFeriaInboxPhotoDrag();
              if (photoIds.length && onDropSessionPhotos) {
                await onDropSessionPhotos(id, photoIds);
                return;
              }
              const file = Array.from(e.dataTransfer?.files || []).find((f) =>
                String(f.type || "")
                  .toLowerCase()
                  .startsWith("image/"),
              );
              if (file) {
                await openAddMaterialFromPhoto(row, file, [], [file]);
              }
            };

            if (suppliersOnly) {
              const accent = row.proveedor
                ? theme.palette.primary.main
                : ACCENT;
              const initials = supplierInitials(name);
              const stand = String(row.Stand || "").trim();
              return (
                <Box
                  key={id}
                  data-feria-supplier-drop={id}
                  onClick={() => focusSupplierInCenter(id)}
                  onDragEnter={acceptSupplierDrop}
                  onDragOver={acceptSupplierDrop}
                  onDragLeave={() => {
                    setSupplierDropActiveId((cur) =>
                      Number(cur) === id ? null : cur,
                    );
                  }}
                  onDrop={handleSupplierFileDrop}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    px: 1.5,
                    py: 1.4,
                    borderRadius: 2.75,
                    cursor: "pointer",
                    bgcolor: dropActive
                      ? alpha(theme.palette.primary.main, 0.12)
                      : expanded
                        ? alpha(theme.palette.primary.main, 0.05)
                        : alpha("#0b1a24", 0.025),
                    border: "1.5px solid",
                    borderColor: dropActive
                      ? theme.palette.primary.main
                      : expanded
                        ? alpha(theme.palette.primary.main, 0.35)
                        : alpha(theme.palette.text.primary, 0.08),
                    borderStyle:
                      mats.length === 0 && !dropActive ? "dashed" : "solid",
                    boxShadow: dropActive
                      ? `0 0 0 3px ${alpha(theme.palette.primary.main, 0.22)}`
                      : `0 1px 4px ${alpha("#0b1a24", 0.05)}`,
                    transition:
                      "box-shadow 0.14s ease, background 0.14s ease, border-color 0.14s ease",
                    "&:hover": {
                      borderColor: dropActive
                        ? theme.palette.primary.main
                        : alpha(theme.palette.text.primary, 0.16),
                      boxShadow: `0 4px 14px ${alpha("#0b1a24", 0.08)}`,
                    },
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1.15}>
                    <Avatar
                      sx={{
                        width: 42,
                        height: 42,
                        flexShrink: 0,
                        fontSize: "0.78rem",
                        fontWeight: 800,
                        bgcolor: alpha(accent, dropActive ? 0.28 : 0.16),
                        color: row.proveedor ? accent : "#c9784a",
                        border: `1px solid ${alpha(accent, 0.25)}`,
                      }}
                    >
                      {initials}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        noWrap
                        sx={{
                          fontWeight: 800,
                          fontSize: "0.92rem",
                          lineHeight: 1.25,
                        }}
                      >
                        {dropActive ? "Suelta aquí" : name}
                      </Typography>
                      <Stack
                        direction="row"
                        spacing={0.6}
                        useFlexGap
                        flexWrap="wrap"
                        sx={{ mt: 0.55 }}
                      >
                        <Chip
                          size="small"
                          icon={
                            row.proveedor ? (
                              <StorefrontOutlinedIcon
                                sx={{ fontSize: "14px !important" }}
                              />
                            ) : (
                              <BusinessOutlinedIcon
                                sx={{ fontSize: "14px !important" }}
                              />
                            )
                          }
                          label={
                            row.proveedor
                              ? t("FeriasPage.kindTrader")
                              : t("FeriasPage.kindMill")
                          }
                          sx={{
                            height: 22,
                            fontWeight: 700,
                            fontSize: "0.68rem",
                            bgcolor: alpha(accent, 0.12),
                            color: row.proveedor ? accent : "#c9784a",
                            "& .MuiChip-icon": {
                              color: "inherit",
                              ml: 0.5,
                            },
                          }}
                        />
                        <Chip
                          size="small"
                          label={`${mats.length} mat.`}
                          sx={{
                            height: 22,
                            fontWeight: 700,
                            fontSize: "0.68rem",
                            bgcolor: mats.length
                              ? alpha(theme.palette.success.main, 0.12)
                              : alpha(theme.palette.text.primary, 0.06),
                            color: mats.length
                              ? theme.palette.success.dark
                              : "text.secondary",
                          }}
                        />
                        {stand ? (
                          <Chip
                            size="small"
                            icon={
                              <PlaceOutlinedIcon
                                sx={{ fontSize: "13px !important" }}
                              />
                            }
                            label={stand}
                            sx={{
                              height: 22,
                              fontWeight: 700,
                              fontSize: "0.68rem",
                              maxWidth: 120,
                              bgcolor: alpha("#0b1a24", 0.04),
                            }}
                          />
                        ) : null}
                      </Stack>
                    </Box>
                    <Stack direction="row" spacing={0.15}>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          openEditSupplier(row);
                        }}
                        sx={{ color: "text.secondary" }}
                      >
                        <EditOutlinedIcon sx={{ fontSize: 17 }} />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={(e) => {
                          e.stopPropagation();
                          requestDeleteSupplier(row);
                        }}
                      >
                        <DeleteOutlineIcon sx={{ fontSize: 17 }} />
                      </IconButton>
                    </Stack>
                  </Stack>
                  {mats.length === 0 ? (
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={0.75}
                      sx={{
                        px: 1,
                        py: 0.7,
                        borderRadius: 1.75,
                        bgcolor: dropActive
                          ? alpha(theme.palette.primary.main, 0.1)
                          : alpha(theme.palette.text.primary, 0.03),
                        color: "text.secondary",
                      }}
                    >
                      <PhotoLibraryOutlinedIcon sx={{ fontSize: 15 }} />
                      <Typography variant="caption" fontWeight={600}>
                        {dropActive
                          ? "Suelta la foto para crear el material"
                          : "Zona de fotos — arrastra desde abajo"}
                      </Typography>
                    </Stack>
                  ) : null}
                </Box>
              );
            }

            return (
              <Accordion
                key={id}
                disableGutters
                elevation={0}
                expanded={expanded || dropActive}
                onChange={(_, isExp) => {
                  setSelectedSupplierId(isExp ? id : null);
                  if (isExp && onFocusItem) onFocusItem("suppliers", id);
                }}
                data-feria-supplier-drop={id}
                onDragEnter={acceptSupplierDrop}
                onDragOver={acceptSupplierDrop}
                onDragLeave={() => {
                  setSupplierDropActiveId((cur) =>
                    Number(cur) === id ? null : cur,
                  );
                }}
                onDrop={handleSupplierFileDrop}
                sx={{
                  border: dropActive
                    ? `2px solid ${theme.palette.primary.main}`
                    : "none",
                  borderRadius: "14px !important",
                  bgcolor: dropActive
                    ? alpha(theme.palette.primary.main, 0.14)
                    : "#fff",
                  boxShadow: dropActive
                    ? `0 0 0 3px ${alpha(theme.palette.primary.main, 0.28)}, 0 4px 14px ${alpha(theme.palette.primary.main, 0.2)}`
                    : `0 1px 3px ${alpha("#0b1a24", 0.07)}`,
                  transform: dropActive ? "scale(1.01)" : "none",
                  "&:before": { display: "none" },
                  overflow: "hidden",
                  transition:
                    "box-shadow 0.12s ease, background 0.12s ease, transform 0.12s ease",
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    minHeight: 64,
                    px: 1.75,
                    py: 0.5,
                    "& .MuiAccordionSummary-content": {
                      my: 1.1,
                      alignItems: "center",
                      gap: 1,
                      overflow: "hidden",
                      mr: 1,
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: 1.75,
                      flexShrink: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: alpha(
                        row.proveedor ? theme.palette.primary.main : ACCENT,
                        0.14,
                      ),
                      color: row.proveedor
                        ? theme.palette.primary.main
                        : "#c9784a",
                    }}
                  >
                    {row.proveedor ? (
                      <StorefrontOutlinedIcon sx={{ fontSize: 18 }} />
                    ) : (
                      <BusinessOutlinedIcon sx={{ fontSize: 18 }} />
                    )}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      noWrap
                      sx={{
                        fontWeight: 800,
                        fontSize: "0.9rem",
                        lineHeight: 1.25,
                      }}
                    >
                      {name}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      noWrap
                      sx={{ display: "block", mt: 0.25 }}
                    >
                      {[
                        row.proveedor
                          ? t("FeriasPage.kindTrader")
                          : t("FeriasPage.kindMill"),
                        t("FeriasPage.supplierMaterialCount", {
                          count: mats.length,
                        }),
                      ].join(" · ")}
                    </Typography>
                  </Box>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      openEditSupplier(row);
                    }}
                    sx={{
                      color: "text.secondary",
                      "&:hover": {
                        bgcolor: alpha(theme.palette.text.primary, 0.06),
                      },
                    }}
                  >
                    <EditOutlinedIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      requestDeleteSupplier(row);
                    }}
                    sx={{
                      color: "error.main",
                      opacity: 0.75,
                      "&:hover": {
                        opacity: 1,
                        bgcolor: alpha(theme.palette.error.main, 0.08),
                      },
                    }}
                  >
                    <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </AccordionSummary>
                <AccordionDetails sx={{ px: 1.75, pt: 0, pb: 1.75 }}>
                  <Button
                    fullWidth
                    size="small"
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => openAddMaterial(row)}
                    sx={{
                      textTransform: "none",
                      fontWeight: 700,
                      mb: 1.25,
                      py: 0.85,
                      borderRadius: 2,
                    }}
                  >
                    {dropActive
                      ? "Suelta aquí"
                      : t("FeriasPage.addMaterialToThisSupplier")}
                  </Button>
                  <Stack spacing={1.1}>
                    {mats.length === 0 ? (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ px: 0.25, py: 0.5 }}
                      >
                        {t("FeriasPage.noMaterialsForSupplier")}
                      </Typography>
                    ) : (
                      mats.map((m) => (
                        <FeriaMaterialCard
                          key={m.id_FeriaMaterial}
                          row={m}
                          highlighted={
                            Number(highlightMaterialId) ===
                            Number(m.id_FeriaMaterial)
                          }
                          showAssignChip
                          assignLabel={t("FeriasPage.moveToSupplier")}
                          onEdit={openEditMaterial}
                          onDelete={async (r) => {
                            await deleteFeriaMaterial(r.id_FeriaMaterial);
                            await notifyChanged();
                          }}
                          onEstadoChange={async (r, Estado) => {
                            await updateFeriaMaterial(r.id_FeriaMaterial, {
                              Estado,
                            });
                            await notifyChanged();
                          }}
                          onAssignClick={(e, r) => {
                            setAssignMenu({
                              anchor: e.currentTarget,
                              feriaMaterialId: r.id_FeriaMaterial,
                            });
                          }}
                          onThumbClick={(src) => setPhotoLightboxSrc(src)}
                        />
                      ))
                    )}
                  </Stack>
                </AccordionDetails>
              </Accordion>
            );
          })}

          {!suppliersOnly && unassignedMaterials.length > 0 ? (
            <Accordion
              disableGutters
              elevation={0}
              expanded={selectedSupplierId === "unassigned"}
              onChange={(_, isExp) => {
                setSelectedSupplierId(isExp ? "unassigned" : null);
              }}
              sx={{
                border: "none",
                borderRadius: "14px !important",
                bgcolor: "#fff",
                boxShadow: `0 1px 3px ${alpha("#0b1a24", 0.07)}`,
                outline: `1px dashed ${alpha(theme.palette.warning.main, 0.55)}`,
                outlineOffset: 0,
                "&:before": { display: "none" },
                overflow: "hidden",
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  minHeight: 64,
                  px: 1.75,
                  "& .MuiAccordionSummary-content": { my: 1.1 },
                }}
              >
                <Box>
                  <Typography sx={{ fontWeight: 800, fontSize: "0.9rem" }}>
                    {t("FeriasPage.unassignedMaterials")}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t("FeriasPage.supplierMaterialCount", {
                      count: unassignedMaterials.length,
                    })}
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 1.75, pt: 0, pb: 1.75 }}>
                <Button
                  fullWidth
                  size="small"
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => openAddMaterial(null)}
                  sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    mb: 1.25,
                    py: 0.85,
                    borderRadius: 2,
                  }}
                >
                  {t("FeriasPage.addMaterial")}
                </Button>
                <Stack spacing={1.1}>
                  {unassignedMaterials.map((m) =>
                    renderMaterialCard(m, { showAssignChip: true }),
                  )}
                </Stack>
              </AccordionDetails>
            </Accordion>
          ) : null}
        </Stack>
      </Box>
    );

  const hidePeople = compactRail && suppliersOnly;

  return (
    <Box
      sx={{
        pb: hidePeople ? 0.5 : 1.5,
        height: "100%",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        gap: 0,
      }}
    >
      {hidePeople ? null : showHeader ? (
        <Box
          sx={{
            px: { xs: 1.5, sm: 2 },
            py: { xs: 1.5, md: 1 },
            mb: 1.5,
            height: { xs: "auto", md: headerHeight },
            minHeight: { xs: "auto", md: headerHeight },
            maxHeight: { xs: "none", md: headerHeight },
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
            background: `linear-gradient(125deg, ${alpha(ACCENT, 0.2)} 0%, ${theme.palette.background.paper} 60%)`,
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: { xs: "stretch", md: "center" },
            justifyContent: "space-between",
            gap: { xs: 1.25, md: 2 },
            flexShrink: 0,
            boxSizing: "border-box",
            overflow: { xs: "visible", md: "hidden" },
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="overline"
              sx={{
                fontWeight: 800,
                color: "text.secondary",
                lineHeight: 1.2,
                display: "block",
              }}
            >
              {edicion.feria?.Nombre}
            </Typography>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: { xs: "1.05rem", md: "1.15rem" },
                lineHeight: 1.25,
              }}
            >
              {edicion.Anio}
              {edicion.Temporada ? ` · ${edicion.Temporada}` : ""}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 0.25 }}
            >
              {[edicion.FechaInicio, edicion.FechaFin]
                .filter(Boolean)
                .join(" → ") || t("FeriasPage.noDates")}
              {edicion.feria?.Ciudad ? ` · ${edicion.feria.Ciudad}` : ""}
            </Typography>
          </Box>
          {peopleControls}
        </Box>
      ) : (
        <Box sx={{ mb: 1.5, flexShrink: 0 }}>{peopleControls}</Box>
      )}

      <Stack
        direction="column"
        spacing={2}
        sx={{
          flex: 1,
          minHeight: 0,
          alignItems: "stretch",
          overflow: "hidden",
        }}
      >
        {catalogBlock}
      </Stack>

      <Menu
        anchorEl={assignMenu.anchor}
        open={Boolean(assignMenu.anchor)}
        onClose={() => setAssignMenu({ anchor: null, feriaMaterialId: null })}
      >
        <MenuItem
          disabled
          sx={{ opacity: 1, fontWeight: 700, fontSize: "0.8rem" }}
        >
          {t("FeriasPage.assignToSupplier")}
        </MenuItem>
        <MenuItem
          onClick={() =>
            assignMaterialToSupplier(assignMenu.feriaMaterialId, null)
          }
        >
          {t("FeriasPage.noSupplierAssigned")}
        </MenuItem>
        {(edicion?.proveedores || []).map((row) => (
          <MenuItem
            key={row.id_FeriaProveedor}
            onClick={() =>
              assignMaterialToSupplier(assignMenu.feriaMaterialId, row)
            }
          >
            {row.proveedor?.N_PROVEEDOR ||
              row.fabrica?.NombreFabrica ||
              t("FeriasPage.unnamed")}
          </MenuItem>
        ))}
      </Menu>

      <Dialog
        open={supplierOpen}
        onClose={() => setSupplierOpen(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, pb: 1 }}>
          {t("FeriasPage.addSupplier")}
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 2 }}>
          <Stack spacing={2.25}>
            <TextField
              select
              size="small"
              label={t("FeriasPage.supplierMode")}
              value={supplierMode}
              onChange={(e) => setSupplierMode(e.target.value)}
              fullWidth
              sx={inputSx}
            >
              <MenuItem value="existing-trader">
                {t("FeriasPage.modeExistingTrader")}
              </MenuItem>
              <MenuItem value="existing-mill">
                {t("FeriasPage.modeExistingMill")}
              </MenuItem>
              <MenuItem value="new">{t("FeriasPage.modeNew")}</MenuItem>
            </TextField>
            {supplierMode === "existing-trader" && (
              <Autocomplete
                options={traders}
                getOptionLabel={(p) =>
                  `${p.N_PROVEEDOR || ""}${p.PAIS ? ` (${p.PAIS})` : ""}`
                }
                onChange={(_, v) =>
                  setSupplierForm((prev) => ({
                    ...prev,
                    CODPROV: v?.CODPROV || "",
                  }))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    label={t("FeriasPage.kindTrader")}
                    sx={inputSx}
                  />
                )}
              />
            )}
            {supplierMode === "existing-mill" && (
              <Autocomplete
                options={mills}
                getOptionLabel={(p) => p.NombreFabrica || ""}
                onChange={(_, v) =>
                  setSupplierForm((prev) => ({
                    ...prev,
                    id_Fabrica: v?.id_Fabrica || "",
                  }))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    label={t("FeriasPage.kindMill")}
                    sx={inputSx}
                  />
                )}
              />
            )}
            {supplierMode === "new" && (
              <>
                {photoButtons("card")}
                {supplierForm.photoFile ? (
                  <Typography variant="caption" color="text.secondary">
                    {t("FeriasPage.photoReady", {
                      name: supplierForm.photoFile.name,
                    })}
                  </Typography>
                ) : null}
                <FormSection
                  icon={BusinessOutlinedIcon}
                  title={t("FeriasPage.sectionSupplier")}
                >
                  <TextField
                    select
                    size="small"
                    label={t("FeriasPage.kind")}
                    value={supplierForm.kind}
                    onChange={(e) =>
                      patchSupplierForm({ kind: e.target.value })
                    }
                    fullWidth
                    sx={inputSx}
                  >
                    <MenuItem value="trader">
                      {t("FeriasPage.kindTrader")}
                    </MenuItem>
                    <MenuItem value="mill">{t("FeriasPage.kindMill")}</MenuItem>
                  </TextField>
                  <TextField
                    size="small"
                    label={t("FeriasPage.labelName")}
                    value={supplierForm.Nombre}
                    onChange={(e) =>
                      patchSupplierForm({ Nombre: e.target.value })
                    }
                    required
                    fullWidth
                    sx={inputSx}
                  />
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                    <TextField
                      size="small"
                      label={t("FeriasPage.labelEmail")}
                      value={supplierForm.Email}
                      onChange={(e) =>
                        patchSupplierForm({ Email: e.target.value })
                      }
                      fullWidth
                      sx={inputSx}
                    />
                    <TextField
                      size="small"
                      label={t("FeriasPage.labelPhone")}
                      value={supplierForm.Telefono}
                      onChange={(e) =>
                        patchSupplierForm({ Telefono: e.target.value })
                      }
                      fullWidth
                      sx={inputSx}
                    />
                  </Stack>
                </FormSection>
                <Divider />
                <FormSection
                  icon={PlaceOutlinedIcon}
                  title={t("FeriasPage.sectionAddress")}
                >
                  <TextField
                    size="small"
                    label={t("ProveedoresPage.labelStreetAddress")}
                    value={supplierForm.Direccion}
                    onChange={(e) =>
                      patchSupplierForm({ Direccion: e.target.value })
                    }
                    fullWidth
                    sx={inputSx}
                  />
                  {supplierForm.kind === "trader" ? (
                    <>
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={1.5}
                      >
                        <TextField
                          size="small"
                          label={t("ProveedoresPage.labelStreetNumber")}
                          value={supplierForm.NVIA}
                          onChange={(e) =>
                            patchSupplierForm({ NVIA: e.target.value })
                          }
                          sx={{ ...inputSx, width: { xs: "100%", sm: 100 } }}
                        />
                        <TextField
                          size="small"
                          label={t("ProveedoresPage.labelPostalShort")}
                          value={supplierForm.CP}
                          onChange={(e) =>
                            patchSupplierForm({ CP: e.target.value })
                          }
                          sx={{ ...inputSx, width: { xs: "100%", sm: 120 } }}
                        />
                        <TextField
                          size="small"
                          label={t("ProveedoresPage.labelCityEn")}
                          value={supplierForm.Ciudad}
                          onChange={(e) =>
                            patchSupplierForm({ Ciudad: e.target.value })
                          }
                          fullWidth
                          sx={inputSx}
                        />
                      </Stack>
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={1.5}
                      >
                        <TextField
                          size="small"
                          label={t("ProveedoresPage.labelProvinceEn")}
                          value={supplierForm.Provincia}
                          onChange={(e) =>
                            patchSupplierForm({ Provincia: e.target.value })
                          }
                          fullWidth
                          sx={inputSx}
                        />
                        <TextField
                          size="small"
                          label={t("FeriasPage.labelCountry")}
                          value={supplierForm.Pais}
                          onChange={(e) =>
                            patchSupplierForm({ Pais: e.target.value })
                          }
                          fullWidth
                          sx={inputSx}
                        />
                      </Stack>
                    </>
                  ) : (
                    <TextField
                      size="small"
                      label={t("FeriasPage.labelCountry")}
                      value={supplierForm.Pais}
                      onChange={(e) =>
                        patchSupplierForm({ Pais: e.target.value })
                      }
                      fullWidth
                      sx={inputSx}
                    />
                  )}
                </FormSection>
              </>
            )}
            <Divider />
            <FormSection
              icon={StorefrontOutlinedIcon}
              title={t("FeriasPage.sectionFairData")}
            >
              <TextField
                size="small"
                label={t("FeriasPage.labelStand")}
                value={supplierForm.Stand}
                onChange={(e) => patchSupplierForm({ Stand: e.target.value })}
                fullWidth
                sx={inputSx}
              />
            </FormSection>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setSupplierOpen(false)}>
            {t("FeriasPage.cancel")}
          </Button>
          <Button variant="contained" onClick={handleSaveSupplier}>
            {t("FeriasPage.save")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={materialOpen}
        onClose={() => {
          if (extracting) return;
          setLinkSessionPhotoIds([]);
          setMaterialOpen(false);
        }}
        fullWidth
        maxWidth="md"
        transitionDuration={200}
        slotProps={{
          backdrop: {
            sx: {
              backdropFilter: "blur(10px)",
              backgroundColor: "rgba(15, 23, 42, 0.42)",
            },
          },
        }}
        PaperProps={{
          sx: {
            borderRadius: 3.5,
            overflow: "hidden",
            border: "1px solid",
            borderColor: alpha("#0b1a24", 0.1),
            boxShadow: `0 24px 64px ${alpha("#0b1a24", 0.22)}`,
            animation: `${dialogRise} 0.34s cubic-bezier(0.22, 1, 0.36, 1)`,
            ...motionSafe,
            maxHeight: "min(92vh, 900px)",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <Box
          sx={{
            px: 2.5,
            pt: 2,
            pb: 1.5,
            flexShrink: 0,
            borderBottom: "1px solid",
            borderColor: "divider",
            background: `linear-gradient(135deg, ${alpha(
              theme.palette.primary.main,
              0.14,
            )} 0%, ${alpha(ACCENT, 0.12)} 55%, ${alpha("#fff", 0.9)} 100%)`,
          }}
        >
          <Stack
            direction="row"
            alignItems="flex-start"
            justifyContent="space-between"
            spacing={1}
          >
            <Stack direction="row" spacing={1.25} alignItems="center">
              <Box
                sx={{
                  width: 42,
                  height: 42,
                  borderRadius: 1.75,
                  display: "grid",
                  placeItems: "center",
                  bgcolor: alpha(theme.palette.primary.main, 0.16),
                  color: "primary.main",
                  animation: `${dialogShimmer} 2.4s ease-in-out infinite`,
                  ...motionSafe,
                }}
              >
                <AutoAwesomeOutlinedIcon sx={{ fontSize: 22 }} />
              </Box>
              <Box>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 800, lineHeight: 1.2 }}
                >
                  {t("FeriasPage.addMaterial")}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {materialForm.fromSessionDrop
                    ? extracting
                      ? "Leyendo ficha con Vision…"
                      : `${
                          materialForm.photoFiles?.length ||
                          (materialForm.photoFile ? 1 : 0)
                        } foto(s) listas · se suben al guardar`
                    : "Completa datos o lee una ficha"}
                </Typography>
              </Box>
            </Stack>
            <IconButton
              size="small"
              onClick={() => {
                if (extracting) return;
                setLinkSessionPhotoIds([]);
                setMaterialOpen(false);
              }}
              disabled={extracting}
            >
              <CloseOutlinedIcon fontSize="small" />
            </IconButton>
          </Stack>
          {extracting ? (
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ mt: 1.25 }}
            >
              <CircularProgress size={14} />
              <Typography variant="caption" color="text.secondary">
                Vision rellenando nombre, composición, specs…
              </Typography>
            </Stack>
          ) : null}
        </Box>
        <DialogContent dividers sx={{ pt: 2.25, flex: 1 }}>
          <Stack spacing={2.25}>
            {materialSaveError ? (
              <Alert severity="error" onClose={() => setMaterialSaveError("")}>
                {materialSaveError}
              </Alert>
            ) : null}
            {materialForm.fromSessionDrop ? (
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 2.5,
                  bgcolor: alpha(theme.palette.primary.main, 0.06),
                }}
              >
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 700, display: "block", mb: 1 }}
                >
                  Fotos de la feria → Materialoteca
                </Typography>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  {sessionDropPreviews.map((p) => (
                    <Box
                      key={p.key}
                      sx={{
                        width: 72,
                        height: 72,
                        borderRadius: 1.5,
                        overflow: "hidden",
                        position: "relative",
                        boxShadow: `0 2px 8px ${alpha("#0b1a24", 0.12)}`,
                        bgcolor: alpha(theme.palette.text.primary, 0.06),
                      }}
                    >
                      <Box
                        component="img"
                        src={p.url}
                        alt=""
                        sx={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          display: "block",
                        }}
                      />
                      {p.isVision ? (
                        <Chip
                          size="small"
                          label="Vision"
                          sx={{
                            position: "absolute",
                            left: 4,
                            bottom: 4,
                            height: 18,
                            fontSize: "0.65rem",
                            fontWeight: 800,
                            bgcolor: alpha("#fff", 0.92),
                          }}
                        />
                      ) : null}
                    </Box>
                  ))}
                </Stack>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mt: 1, lineHeight: 1.4 }}
                >
                  La 1ª alimentó Vision (ficha). Todas se suben como fotos del
                  material al guardar — sin volver a elegir imagen.
                </Typography>
              </Box>
            ) : null}

            {!materialForm.fromSessionDrop ? (
              <Autocomplete
                options={materiales}
                getOptionLabel={(m) => m.NombreMaterial || ""}
                onChange={(_, v) =>
                  setMaterialForm((prev) => ({
                    ...prev,
                    id_Materialoteca: v?.Id || "",
                  }))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    label={t("FeriasPage.existingMaterial")}
                    helperText={t("FeriasPage.existingMaterialHint")}
                    sx={inputSx}
                  />
                )}
              />
            ) : null}
            <Autocomplete
              options={supplierOptions}
              value={selectedMaterialSupplier}
              onChange={(_, opt) =>
                applySupplierToMaterialForm(opt, patchMaterialForm)
              }
              getOptionLabel={(o) =>
                o
                  ? `${o.label}${
                      o.kind === "mill"
                        ? ` (${t("FeriasPage.kindMill")})`
                        : ` (${t("FeriasPage.kindTrader")})`
                    }`
                  : ""
              }
              isOptionEqualToValue={(a, b) => a?.key === b?.key}
              noOptionsText={t("FeriasPage.noEditionSuppliersForMaterial")}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  label={t("FeriasPage.materialSupplier")}
                  helperText={
                    materialForm.fromSessionDrop
                      ? undefined
                      : t("FeriasPage.materialSupplierOptionalHint")
                  }
                  sx={inputSx}
                />
              )}
            />
            {!materialForm.id_Materialoteca && (
              <>
                {!materialForm.fromSessionDrop ? (
                  <>
                    <FormSection
                      icon={ImageOutlinedIcon}
                      title={t("FeriasPage.fromPhoto")}
                    >
                      <Typography variant="body2" color="text.secondary">
                        {t("FeriasPage.extractMaterialHint")}
                      </Typography>
                      {photoButtons("material")}
                      {materialForm.extractName ? (
                        <Typography variant="caption" color="text.secondary">
                          {t("FeriasPage.dataReadFrom", {
                            name: materialForm.extractName,
                          })}
                        </Typography>
                      ) : null}
                    </FormSection>
                    <Divider />
                    <FormSection
                      icon={PhotoCameraOutlinedIcon}
                      title={t("MaterialotecaPage.sectionPhoto")}
                    >
                      <Typography variant="body2" color="text.secondary">
                        {t("FeriasPage.materialPhotoHint")}
                      </Typography>
                      {photoButtons("material-photo", {
                        takeLabel: t("FeriasPage.takeMaterialPhoto"),
                        galleryLabel: t("FeriasPage.chooseMaterialPhoto"),
                      })}
                      {materialForm.photoFile ? (
                        <Typography variant="caption" color="text.secondary">
                          {t("FeriasPage.photoReady", {
                            name: materialForm.photoFile.name,
                          })}
                        </Typography>
                      ) : null}
                    </FormSection>
                    <Divider />
                  </>
                ) : null}
                <FormSection
                  icon={CategoryOutlinedIcon}
                  title={t("FeriasPage.sectionMaterial")}
                >
                  <TextField
                    size="small"
                    label={t("MaterialotecaPage.labelMaterialName")}
                    value={materialForm.NombreMaterial}
                    onChange={(e) =>
                      patchMaterialForm({ NombreMaterial: e.target.value })
                    }
                    required
                    fullWidth
                    sx={inputSx}
                  />
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                    <TextField
                      select
                      size="small"
                      label={t("MaterialotecaPage.labelGroup")}
                      value={materialForm.ID_GRUPO}
                      onChange={(e) =>
                        patchMaterialForm({
                          ID_GRUPO: e.target.value,
                          TipoMaterial: "",
                        })
                      }
                      fullWidth
                      sx={inputSx}
                    >
                      {grupos.map((g) => (
                        <MenuItem key={g.ID_GRUPO} value={g.ID_GRUPO}>
                          {g.GRUPO}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      select
                      size="small"
                      label={t("MaterialotecaPage.labelSubgroup")}
                      value={materialForm.TipoMaterial}
                      onChange={(e) =>
                        patchMaterialForm({ TipoMaterial: e.target.value })
                      }
                      disabled={!materialForm.ID_GRUPO}
                      fullWidth
                      sx={inputSx}
                    >
                      {subgrupos.map((s) => (
                        <MenuItem key={s.ID_SUBGRUPO} value={s.ID_SUBGRUPO}>
                          {s.SUBGRUPO}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Stack>
                  <TextField
                    size="small"
                    label={t("MaterialotecaPage.labelComposition")}
                    value={materialForm.Composicion}
                    onChange={(e) =>
                      patchMaterialForm({ Composicion: e.target.value })
                    }
                    fullWidth
                    sx={inputSx}
                  />
                </FormSection>
                <Divider />
                <FormSection
                  icon={StraightenOutlinedIcon}
                  title={t("MaterialotecaPage.sectionSpecs")}
                >
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                    <TextField
                      size="small"
                      type="number"
                      label={t("MaterialotecaPage.labelWeight")}
                      value={materialForm.Peso}
                      onChange={(e) =>
                        patchMaterialForm({ Peso: e.target.value })
                      }
                      fullWidth
                      sx={inputSx}
                    />
                    <TextField
                      size="small"
                      type="number"
                      label={t("MaterialotecaPage.labelWidth")}
                      value={materialForm.AnchoTirada}
                      onChange={(e) =>
                        patchMaterialForm({ AnchoTirada: e.target.value })
                      }
                      fullWidth
                      sx={inputSx}
                    />
                    <TextField
                      size="small"
                      type="number"
                      label={t("MaterialotecaPage.labelMoq")}
                      value={materialForm.MinimumOrderQty}
                      onChange={(e) =>
                        patchMaterialForm({ MinimumOrderQty: e.target.value })
                      }
                      fullWidth
                      sx={inputSx}
                    />
                    <TextField
                      size="small"
                      type="number"
                      label={t("MaterialotecaPage.labelPrice")}
                      value={materialForm.Precio}
                      onChange={(e) =>
                        patchMaterialForm({ Precio: e.target.value })
                      }
                      inputProps={{ step: "0.01" }}
                      fullWidth
                      sx={inputSx}
                    />
                  </Stack>
                  <TextField
                    size="small"
                    label={t("FeriasPage.labelMillName")}
                    value={materialForm.MillName}
                    onChange={(e) =>
                      patchMaterialForm({ MillName: e.target.value })
                    }
                    fullWidth
                    sx={inputSx}
                  />
                </FormSection>
              </>
            )}
            <FormControlLabel
              control={
                <Switch
                  checked={materialForm.pedir}
                  onChange={(e) =>
                    patchMaterialForm({ pedir: e.target.checked })
                  }
                />
              }
              label={t("FeriasPage.orderSample")}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 1.75, gap: 1 }}>
          <Button
            onClick={() => {
              setLinkSessionPhotoIds([]);
              setMaterialSaveError("");
              setMaterialOpen(false);
            }}
            disabled={extracting || materialSaving}
            sx={{ textTransform: "none", fontWeight: 700 }}
          >
            {t("FeriasPage.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveMaterial}
            disabled={extracting || materialSaving}
            startIcon={
              materialSaving ? (
                <CircularProgress size={14} color="inherit" />
              ) : null
            }
            sx={{
              textTransform: "none",
              fontWeight: 800,
              px: 2.5,
              borderRadius: 2,
              boxShadow: "none",
              "&:hover": { boxShadow: "none" },
            }}
          >
            {materialForm.fromSessionDrop
              ? "Guardar y subir fotos"
              : t("FeriasPage.save")}
          </Button>
        </DialogActions>
      </Dialog>

      <ExpenseUserWebcamDialog
        open={webcamOpen}
        onClose={() => setWebcamOpen(false)}
        t={t}
        onCapture={async (file) => {
          if (webcamKind === "material-photo") {
            patchMaterialForm({ photoFile: file });
            return;
          }
          await handlePhotoExtract(file, webcamKind);
        }}
      />

      <Dialog
        open={Boolean(editSupplier)}
        onClose={() => setEditSupplier(null)}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, pb: 1 }}>
          {t("FeriasPage.editSupplier")}
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 2 }}>
          <Stack spacing={2.25}>
            <FormSection
              icon={BusinessOutlinedIcon}
              title={t("FeriasPage.sectionSupplier")}
            >
              <TextField
                size="small"
                label={t("FeriasPage.labelName")}
                value={editSupplierForm.Nombre}
                onChange={(e) => patchEditSupplier({ Nombre: e.target.value })}
                required
                fullWidth
                sx={inputSx}
              />
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                <TextField
                  size="small"
                  label={t("FeriasPage.labelEmail")}
                  value={editSupplierForm.Email}
                  onChange={(e) => patchEditSupplier({ Email: e.target.value })}
                  fullWidth
                  sx={inputSx}
                />
                <TextField
                  size="small"
                  label={t("FeriasPage.labelPhone")}
                  value={editSupplierForm.Telefono}
                  onChange={(e) =>
                    patchEditSupplier({ Telefono: e.target.value })
                  }
                  fullWidth
                  sx={inputSx}
                />
              </Stack>
            </FormSection>

            <Divider />

            <FormSection
              icon={PlaceOutlinedIcon}
              title={t("FeriasPage.sectionAddress")}
            >
              <TextField
                size="small"
                label={t("ProveedoresPage.labelStreetAddress")}
                value={editSupplierForm.Direccion}
                onChange={(e) =>
                  patchEditSupplier({ Direccion: e.target.value })
                }
                fullWidth
                sx={inputSx}
              />
              {editSupplierForm.isTrader ? (
                <>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                    <TextField
                      size="small"
                      label={t("ProveedoresPage.labelStreetNumber")}
                      value={editSupplierForm.NVIA}
                      onChange={(e) =>
                        patchEditSupplier({ NVIA: e.target.value })
                      }
                      sx={{ ...inputSx, width: { xs: "100%", sm: 100 } }}
                    />
                    <TextField
                      size="small"
                      label={t("ProveedoresPage.labelPostalShort")}
                      value={editSupplierForm.CP}
                      onChange={(e) =>
                        patchEditSupplier({ CP: e.target.value })
                      }
                      sx={{ ...inputSx, width: { xs: "100%", sm: 120 } }}
                    />
                    <TextField
                      size="small"
                      label={t("ProveedoresPage.labelCityEn")}
                      value={editSupplierForm.Ciudad}
                      onChange={(e) =>
                        patchEditSupplier({ Ciudad: e.target.value })
                      }
                      fullWidth
                      sx={inputSx}
                    />
                  </Stack>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                    <TextField
                      size="small"
                      label={t("ProveedoresPage.labelProvinceEn")}
                      value={editSupplierForm.Provincia}
                      onChange={(e) =>
                        patchEditSupplier({ Provincia: e.target.value })
                      }
                      fullWidth
                      sx={inputSx}
                    />
                    <TextField
                      size="small"
                      label={t("FeriasPage.labelCountry")}
                      value={editSupplierForm.Pais}
                      onChange={(e) =>
                        patchEditSupplier({ Pais: e.target.value })
                      }
                      fullWidth
                      sx={inputSx}
                    />
                  </Stack>
                </>
              ) : (
                <TextField
                  size="small"
                  label={t("FeriasPage.labelCountry")}
                  value={editSupplierForm.Pais}
                  onChange={(e) => patchEditSupplier({ Pais: e.target.value })}
                  fullWidth
                  sx={inputSx}
                />
              )}
            </FormSection>

            <Divider />

            <FormSection
              icon={StorefrontOutlinedIcon}
              title={t("FeriasPage.sectionFairData")}
            >
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                <TextField
                  size="small"
                  label={t("FeriasPage.labelStand")}
                  value={editSupplierForm.Stand}
                  onChange={(e) => patchEditSupplier({ Stand: e.target.value })}
                  fullWidth
                  sx={inputSx}
                />
              </Stack>
              <TextField
                size="small"
                label={t("FeriasPage.labelNotes")}
                value={editSupplierForm.Notas}
                onChange={(e) => patchEditSupplier({ Notas: e.target.value })}
                fullWidth
                multiline
                minRows={2}
                sx={inputSx}
              />
            </FormSection>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditSupplier(null)}>
            {t("FeriasPage.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={async () => {
              if (!editSupplier) return;
              await updateFeriaProveedor(editSupplier.id_FeriaProveedor, {
                Nombre: editSupplierForm.Nombre,
                Pais: editSupplierForm.Pais,
                Email: editSupplierForm.Email,
                Telefono: editSupplierForm.Telefono,
                Direccion: editSupplierForm.Direccion,
                NVIA: editSupplierForm.NVIA,
                CP: editSupplierForm.CP,
                Ciudad: editSupplierForm.Ciudad,
                Provincia: editSupplierForm.Provincia,
                Stand: editSupplierForm.Stand,
                Notas: editSupplierForm.Notas,
              });
              setEditSupplier(null);
              await notifyChanged();
            }}
          >
            {t("FeriasPage.save")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(editMaterial)}
        onClose={() => setEditMaterial(null)}
        fullWidth
        maxWidth="md"
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, pb: 1 }}>
          {t("FeriasPage.editMaterial")}
        </DialogTitle>
        <DialogContent dividers sx={{ pt: 2 }}>
          <Stack spacing={2.25}>
            <FormSection
              icon={CategoryOutlinedIcon}
              title={t("FeriasPage.sectionMaterial")}
            >
              <Autocomplete
                options={supplierOptions}
                value={selectedEditMaterialSupplier}
                getOptionLabel={(o) =>
                  o
                    ? `${o.label}${
                        o.kind === "mill"
                          ? ` (${t("FeriasPage.kindMill")})`
                          : ` (${t("FeriasPage.kindTrader")})`
                      }`
                    : ""
                }
                isOptionEqualToValue={(a, b) => a?.key === b?.key}
                onChange={(_, v) =>
                  applySupplierToMaterialForm(v, patchEditMaterial)
                }
                noOptionsText={t("FeriasPage.noEditionSuppliersForMaterial")}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    required
                    label={t("FeriasPage.materialSupplier")}
                    helperText={t("FeriasPage.materialSupplierHint")}
                    sx={inputSx}
                  />
                )}
              />
              <TextField
                size="small"
                label={t("MaterialotecaPage.labelMaterialName")}
                value={editMaterialForm.NombreMaterial}
                onChange={(e) =>
                  patchEditMaterial({ NombreMaterial: e.target.value })
                }
                required
                fullWidth
                sx={inputSx}
              />
              <TextField
                size="small"
                label={t("MaterialotecaPage.labelComposition")}
                value={editMaterialForm.Composicion}
                onChange={(e) =>
                  patchEditMaterial({ Composicion: e.target.value })
                }
                fullWidth
                sx={inputSx}
              />
              <TextField
                size="small"
                label={t("FeriasPage.labelMillName")}
                value={editMaterialForm.MillName}
                onChange={(e) =>
                  patchEditMaterial({ MillName: e.target.value })
                }
                fullWidth
                sx={inputSx}
              />
            </FormSection>

            <Divider />

            <FormSection
              icon={StraightenOutlinedIcon}
              title={t("MaterialotecaPage.sectionSpecs")}
            >
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                <TextField
                  size="small"
                  type="number"
                  label={t("MaterialotecaPage.labelWeight")}
                  value={editMaterialForm.Peso}
                  onChange={(e) => patchEditMaterial({ Peso: e.target.value })}
                  fullWidth
                  sx={inputSx}
                />
                <TextField
                  size="small"
                  type="number"
                  label={t("MaterialotecaPage.labelWidth")}
                  value={editMaterialForm.AnchoTirada}
                  onChange={(e) =>
                    patchEditMaterial({ AnchoTirada: e.target.value })
                  }
                  fullWidth
                  sx={inputSx}
                />
                <TextField
                  size="small"
                  type="number"
                  label={t("MaterialotecaPage.labelMoq")}
                  value={editMaterialForm.MinimumOrderQty}
                  onChange={(e) =>
                    patchEditMaterial({ MinimumOrderQty: e.target.value })
                  }
                  fullWidth
                  sx={inputSx}
                />
                <TextField
                  size="small"
                  type="number"
                  label={t("MaterialotecaPage.labelPrice")}
                  value={editMaterialForm.Precio}
                  onChange={(e) =>
                    patchEditMaterial({ Precio: e.target.value })
                  }
                  inputProps={{ step: "0.01" }}
                  fullWidth
                  sx={inputSx}
                />
              </Stack>
            </FormSection>

            <Divider />

            <FormSection
              icon={PhotoCameraOutlinedIcon}
              title={t("MaterialotecaPage.sectionPhoto")}
            >
              {(editMaterialForm.Fotos || []).length > 0 ? (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {(editMaterialForm.Fotos || []).map((foto, idx) => {
                    const src = materialFotoPublicUrl(foto.Archivo);
                    if (!src) return null;
                    return (
                      <Box
                        key={`${foto.id_MaterialotecaFoto || "legacy"}-${foto.Archivo}-${idx}`}
                        sx={{
                          position: "relative",
                          width: 88,
                          height: 88,
                          borderRadius: 2,
                          overflow: "hidden",
                          border: `1px solid ${alpha(theme.palette.divider, 0.85)}`,
                        }}
                      >
                        <Box
                          component="img"
                          src={src}
                          alt=""
                          onClick={() => setPhotoLightboxSrc(src)}
                          sx={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            cursor: "zoom-in",
                          }}
                        />
                        <IconButton
                          size="small"
                          disabled={editPhotoBusy}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveEditMaterialPhoto(foto);
                          }}
                          sx={{
                            position: "absolute",
                            top: 4,
                            right: 4,
                            bgcolor: "rgba(0,0,0,0.5)",
                            color: "#fff",
                            p: "2px",
                            "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
                          }}
                          aria-label={t("FeriasPage.removePhoto")}
                        >
                          <DeleteOutlineIcon fontSize="inherit" />
                        </IconButton>
                      </Box>
                    );
                  })}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {t("FeriasPage.noMaterialPhotos")}
                </Typography>
              )}
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                <Button
                  startIcon={<PhotoCameraOutlinedIcon />}
                  variant="outlined"
                  disabled={editPhotoBusy}
                  onClick={pickEditMaterialPhotos}
                  sx={{ textTransform: "none", fontWeight: 700 }}
                >
                  {editPhotoBusy
                    ? t("FeriasPage.uploadingPhotos")
                    : t("FeriasPage.addMaterialPhotos")}
                </Button>
              </Stack>
            </FormSection>

            <Divider />

            <FormSection
              icon={NotesOutlinedIcon}
              title={t("FeriasPage.sectionFairData")}
            >
              <TextField
                select
                size="small"
                label={t("FeriasPage.estadoLabel")}
                value={editMaterialForm.Estado}
                onChange={(e) => patchEditMaterial({ Estado: e.target.value })}
                fullWidth
                sx={inputSx}
              >
                {MUESTRA_ESTADOS.map((st) => (
                  <MenuItem key={st} value={st}>
                    {t(`FeriasPage.estado.${st}`)}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                size="small"
                label={t("FeriasPage.labelNotes")}
                value={editMaterialForm.Notas}
                onChange={(e) => patchEditMaterial({ Notas: e.target.value })}
                fullWidth
                multiline
                minRows={2}
                sx={inputSx}
              />
            </FormSection>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditMaterial(null)}>
            {t("FeriasPage.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={async () => {
              if (!editMaterial) return;
              await updateFeriaMaterial(editMaterial.id_FeriaMaterial, {
                NombreMaterial: editMaterialForm.NombreMaterial,
                Composicion: editMaterialForm.Composicion,
                MillName: editMaterialForm.MillName,
                Proveedor: editMaterialForm.Proveedor || null,
                CODPROV: editMaterialForm.Proveedor || null,
                id_Fabrica: editMaterialForm.id_Fabrica || null,
                Peso:
                  editMaterialForm.Peso === "" ? null : editMaterialForm.Peso,
                AnchoTirada:
                  editMaterialForm.AnchoTirada === ""
                    ? null
                    : editMaterialForm.AnchoTirada,
                MinimumOrderQty:
                  editMaterialForm.MinimumOrderQty === ""
                    ? null
                    : editMaterialForm.MinimumOrderQty,
                Precio:
                  editMaterialForm.Precio === ""
                    ? null
                    : editMaterialForm.Precio,
                Estado: editMaterialForm.Estado,
                Notas: editMaterialForm.Notas,
              });
              setEditMaterial(null);
              await notifyChanged();
            }}
          >
            {t("FeriasPage.save")}
          </Button>
        </DialogActions>
      </Dialog>

      <FeriaDeletePhotoDialog
        open={Boolean(supplierDeleteTarget)}
        variant="supplier"
        itemLabel={
          supplierDeleteTarget?.proveedor?.N_PROVEEDOR ||
          supplierDeleteTarget?.fabrica?.NombreFabrica ||
          ""
        }
        deleting={deletingSupplier}
        onClose={() => {
          if (!deletingSupplier) setSupplierDeleteTarget(null);
        }}
        onConfirm={confirmDeleteSupplier}
      />

      <MaterialotecaPhotoLightbox
        open={Boolean(photoLightboxSrc)}
        src={photoLightboxSrc}
        alt={
          editMaterialForm.NombreMaterial ||
          editMaterial?.material?.NombreMaterial ||
          ""
        }
        onClose={() => setPhotoLightboxSrc(null)}
        closeAriaLabel={t("MaterialotecaPage.photoLightboxCloseAria")}
      />
    </Box>
  );
};

export default FeriaEdicionPanel;
